import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type FlaggedContent, type ContentAction } from '@/lib/api'

type FilterStatus = 'pending' | 'reviewed' | 'all'

export function ModerationPage() {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending')
  const queryClient = useQueryClient()

  const {
    data: flaggedItems,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['moderation', 'flagged', filterStatus],
    queryFn: () =>
      adminApi.moderation.getFlaggedContent({ status: filterStatus }),
  })

  const actionMutation = useMutation({
    mutationFn: ({
      contentId,
      action,
    }: {
      contentId: string
      action: ContentAction
    }) => adminApi.moderation.takeAction(contentId, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['moderation'] })
    },
  })

  function handleAction(contentId: string, action: ContentAction) {
    actionMutation.mutate({ contentId, action })
  }

  return (
    <div className="px-8 py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Content Moderation
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Review flagged content and user reports. Content is never displayed
          — only metadata and flag reasons are shown.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="mb-6 flex space-x-1 rounded-lg bg-gray-100 p-1">
        {(['pending', 'reviewed', 'all'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              filterStatus === status
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Content list */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load flagged content. Please try again.
        </div>
      )}

      {flaggedItems && flaggedItems.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">
          No flagged content to review.
        </div>
      )}

      {flaggedItems && flaggedItems.length > 0 && (
        <div className="space-y-4">
          {flaggedItems.map((item: FlaggedContent) => (
            <div
              key={item.id}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                      {item.flagReason}
                    </span>
                    <span className="text-xs text-gray-500">
                      {item.reportCount} report
                      {item.reportCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    Content type:{' '}
                    <span className="font-medium">{item.contentType}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Reported by {item.reporterCount} user
                    {item.reporterCount !== 1 ? 's' : ''} &middot; First
                    reported:{' '}
                    {new Date(item.firstReportedAt).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    Content ID: {item.id}
                  </p>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAction(item.id, 'dismiss')}
                    disabled={actionMutation.isPending}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleAction(item.id, 'warn')}
                    disabled={actionMutation.isPending}
                    className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs font-medium text-yellow-800 shadow-sm transition-colors hover:bg-yellow-100 disabled:opacity-50"
                  >
                    Warn User
                  </button>
                  <button
                    onClick={() => handleAction(item.id, 'remove')}
                    disabled={actionMutation.isPending}
                    className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 shadow-sm transition-colors hover:bg-red-100 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
